import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { cn } from '@/libs/utils/cn';
import type { IMetrics } from '@/types/api';
import type { MetricsList } from '../constants';

const containerVariants = cva('flex justify-between', {
  variants: {
    variant: {
      normal: '',
      main: 'flex-col',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const labelVariants = cva('text-sm', {
  variants: {
    variant: {
      normal: 'text-muted-foreground',
      main: 'text-primary-600',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const valueContainerVariants = cva('font-bold text-sm text-primary-700 flex', {
  variants: {
    variant: {
      normal: 'flex-col items-end',
      main: 'flex-row items-center text-[2.125rem] tracking-tight font-light',
    },
  },
  defaultVariants: { variant: 'normal' },
});

const iconVariants = cva('inline-flex items-center mr-1.5');

const unitVariants = cva('', {
  variants: {
    variant: {
      normal: '',
      main: 'ml-0.5',
    },
  },
  defaultVariants: { variant: 'normal' },
});

interface PropsMetricItem extends VariantProps<typeof containerVariants> {
  label: string;
  metricKey: MetricsList | string;
  metrics: IMetrics[] | null;
  formatValue?: (value: number) => string;
  icon?: ReactNode;
}

export function MetricItem({
  label,
  metricKey,
  metrics,
  variant = 'normal',
  formatValue,
  icon,
}: PropsMetricItem) {
  const metric = metrics?.find((m) => m.label === metricKey);
  const formattedValue =
    metric && metric.value != null
      ? formatValue
        ? formatValue(metric.value)
        : metric.value.toLocaleString()
      : undefined;

  return (
    <div className={cn(containerVariants({ variant }))}>
      <span className={labelVariants({ variant })}>{label}</span>
      <div className={valueContainerVariants({ variant })}>
        {icon && <span className={iconVariants()}>{icon}</span>}
        <span>{formattedValue}</span>
        {metric?.unit && <span className={unitVariants({ variant })}>{metric.unit}</span>}
      </div>
    </div>
  );
}
